import { useState, type FormEvent } from 'react'
import { useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getGuardianLink, giveGuardianConsent } from '../api/endpoints'
import { ApiError } from '../api/client'
import { ErrorNotice, PublicShell } from '../components'

/** Opened from the email link; no sign-in, the link itself is the credential. */
export default function GuardianConsentPage() {
  const { token = '' } = useParams()
  const [name, setName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const link = useQuery({ queryKey: ['guardian-link', token], queryFn: () => getGuardianLink(token), retry: false })
  const agree = useMutation({ mutationFn: () => giveGuardianConsent(token, link.data!.text.version, name.trim()) })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    agree.mutate()
  }

  if (link.isPending) return <PublicShell><p role="status">Loading…</p></PublicShell>
  if (link.isError) {
    const e = link.error
    const used = e instanceof ApiError && e.status === 410
    return (
      <PublicShell>
        <h1>{used ? 'This link can no longer be used' : 'This link is not valid'}</h1>
        <p>{used ? 'It has already been used or has expired. Ask the candidate to send a new link from their account.' : 'Please check the link in your email, or ask the candidate to send it again.'}</p>
      </PublicShell>
    )
  }
  if (agree.isSuccess) {
    return (
      <PublicShell>
        <div className="notice ok" role="status">
          <h1>Thank you</h1>
          <p>Your consent has been recorded. {link.data.candidateName} can now take the assessments. You can close this page.</p>
        </div>
      </PublicShell>
    )
  }
  const d = link.data
  return (
    <PublicShell>
      <h1>Parent or guardian consent</h1>
      <p>{d.institution} has asked for your consent for <strong>{d.candidateName}</strong> to take its admission assessments.</p>
      <form className="card" onSubmit={submit}>
        <div className="scroll-text" tabIndex={0} aria-label="Consent text">{d.text.body}</div>
        <label className="field">Your full name
          <input type="text" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="check">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>I am the parent or guardian of {d.candidateName}. I have read this and I agree.</span>
        </label>
        <ErrorNotice error={agree.error} />
        <button className="primary" type="submit" disabled={!agreed || !name.trim() || agree.isPending}>{agree.isPending ? 'Saving…' : 'I agree'}</button>
      </form>
    </PublicShell>
  )
}
